<div class="main-container-position">
  <div class="container-width">
    <h2 class="text-lg font-medium text-gray-900 mb-4" i18n>Firma einladen</h2>
    <form
      [formGroup]="emailForm"
      (ngSubmit)="onSubmit()"
      class="max-w-md flex flex-row items-center"
    >
      <div class="flex flex-col">
        <div class="flex flex-row items-center">
          <mat-form-field class="w-full">
            <mat-label i18n>Email</mat-label>
            <input matInput type="email" formControlName="email" />
          </mat-form-field>
          <button
            type="submit"
            [disabled]="!emailForm.valid || loadingState.sending"
            class="ml-4 button-blue"
            data-testid="send-invitation-button"
          >
            @if (loadingState.sending) {
              <span class="animate-pulse">Lade ...</span>
            } @else {
              Einladen
            }
          </button>
        </div>

        <div class="flex items-center mb-4">
          <hr class="flex-grow" />
          <span class="px-4" i18n>oder</span>
          <hr class="flex-grow" />
        </div>

        <button
          type="button"
          class="button-blue"
          (click)="openCreateCompany()"
          i18n
          data-testid="create-company-button"
        >
          Firma manuell erstellen
        </button>
      </div>
    </form>

    <div class="mt-8">
      <div class="list-header">
        <h2 class="header-title" i18n>Alle Firmen</h2>

        <mat-form-field class="search-field">
          <mat-label i18n>Suche</mat-label>
          <input
            matInput
            type="search"
            [value]="searchTerm"
            (input)="onSearchChange($any($event.target).value)"
            placeholder="Firma, Email ..."
            i18n-placeholder
          />
        </mat-form-field>

        <div class="filters">
          <span class="filters-label" i18n>Filter:</span>
          <mat-chip-listbox class="filters-chips">
            @for (status of statusValues; track status) {
              <mat-chip-option
                [selected]="selectedStatus === status"
                (click)="onStatusFilterChange(status)"
                [ngClass]="{
                  'bg-yellow-100 text-yellow-800':
                    status === CompanyInvitationStatus.PENDING,
                  'bg-green-100 text-green-800':
                    status === CompanyInvitationStatus.ACCEPTED,
                  'bg-blue-100 text-blue-800':
                    status === CompanyInvitationStatus.CREATED_NO_EMAIL,
                }"
              >
                {{ getStatusText(status) }}
              </mat-chip-option>
            }
          </mat-chip-listbox>
        </div>
      </div>

      @if (loadingState.loading) {
        <app-loading-indication
          displayText="Lade Firmen..."
          size="large"
        ></app-loading-indication>
      } @else {
        <table mat-table [dataSource]="dataSource" matSort class="w-full">
          <!-- Add this no data row template -->
          <tr class="mat-row" *matNoDataRow>
            <td
              i18n
              class="mat-cell text-center py-4"
              [attr.colspan]="displayedColumns.length"
            >
              Keine Firmen gefunden
            </td>
          </tr>

          <!-- Company Column -->
          <ng-container matColumnDef="company">
            <th mat-header-cell *matHeaderCellDef mat-sort-header i18n>
              Firma
            </th>
            <td mat-cell *matCellDef="let invitation">
              {{ invitation.companyName || "—" }}
            </td>
          </ng-container>

          <!-- Email Column -->
          <ng-container matColumnDef="email">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Email</th>
            <td mat-cell *matCellDef="let invitation">
              {{ invitation.email }}
            </td>
          </ng-container>

          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let invitation">
              <mat-chip-listbox>
                <mat-chip
                  [ngClass]="{
                    'bg-yellow-100 text-yellow-800':
                      invitation.status === CompanyInvitationStatus.PENDING,
                    'bg-green-100 text-green-800':
                      invitation.status === CompanyInvitationStatus.ACCEPTED,
                    'bg-blue-100 text-blue-800':
                      invitation.status ===
                      CompanyInvitationStatus.CREATED_NO_EMAIL,
                  }"
                >
                  {{ getStatusText(invitation.status) }}
                </mat-chip>
              </mat-chip-listbox>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let invitation">
              <button
                mat-icon-button
                [matMenuTriggerFor]="menu"
                aria-label="Actions"
                data-testid="actions-button"
              >
                <span class="material-symbols-outlined">more_vert</span>
              </button>
              <mat-menu #menu="matMenu">
                <button
                  mat-menu-item
                  (click)="editCompany(invitation)"
                  data-testid="edit-company-button"
                >
                  <div class="flex items-center">
                    <span class="mr-2 material-symbols-outlined">edit</span>
                    <span i18n>Firma bearbeiten</span>
                  </div>
                </button>
                @if (invitation.status === CompanyInvitationStatus.PENDING) {
                  <button mat-menu-item (click)="resendInvitation(invitation)">
                    <div class="flex items-center">
                      <span class="mr-2 material-symbols-outlined">send</span>
                      <span i18n>Einladung erneut senden</span>
                    </div>
                  </button>
                }
                @if (
                  invitation.status === CompanyInvitationStatus.CREATED_NO_EMAIL
                ) {
                  <button
                    mat-menu-item
                    (click)="sendInvitation(invitation)"
                    data-testid="send-invitation-button"
                  >
                    <div class="flex items-center">
                      <span class="mr-2 material-symbols-outlined">send</span>
                      <span i18n>Einladung senden</span>
                    </div>
                  </button>
                }
                <button
                  class="flex items-center"
                  mat-menu-item
                  (click)="copyEmail(invitation.email)"
                  data-testid="copy-email-button"
                >
                  <div class="flex items-center">
                    <span class="mr-2 material-symbols-outlined"
                      >content_copy</span
                    >
                    <span i18n>Email kopieren</span>
                  </div>
                </button>

                @if (invitation.status !== CompanyInvitationStatus.ACCEPTED) {
                  <button
                    class="flex items-center"
                    mat-menu-item
                    (click)="deleteInvitation(invitation)"
                    data-testid="delete-invitation-button"
                  >
                    <div class="flex items-center">
                      <span class="mr-2 material-symbols-outlined">delete</span>
                      <span i18n>Einladung Löschen</span>
                    </div>
                  </button>
                }
              </mat-menu>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      }
    </div>
  </div>
</div>
